import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Film } from '@/types/film';
import { allRegions, categoryMeta } from '@/mocks/categories';
import { filterFilms, sortFilms, type FilmQuery } from '@/lib/catalog';
import FilmCard from '@/components/feature/FilmCard';

const PAGE_SIZE = 8;

const sortOptions: { key: NonNullable<FilmQuery['sort']>; label: string }[] = [
  { key: 'updated', label: '最近更新' },
  { key: 'hot', label: '热度最高' },
  { key: 'title', label: '名称排序' },
];

interface FilterPanelProps {
  genres: string[];
  genre: string;
  region: string;
  year: string;
  onChange: (patch: { genre?: string; region?: string; year?: string }) => void;
  onReset: () => void;
  activeCount: number;
}

function FilterGroup({
  title,
  options,
  value,
  onPick,
}: {
  title: string;
  options: { value: string; label: string }[];
  value: string;
  onPick: (value: string) => void;
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-foreground-400 tracking-wide mb-2">{title}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={`${title}-${option.value}`}
            type="button"
            onClick={() => onPick(option.value)}
            className={`px-2.5 py-1 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
              value === option.value
                ? 'bg-accent-500 text-primary-950 font-semibold'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-950'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function FilterPanel({ genres, genre, region, year, onChange, onReset, activeCount }: FilterPanelProps) {
  return (
    <div className="space-y-5">
      <FilterGroup
        title="类型"
        value={genre}
        onPick={(value) => onChange({ genre: value })}
        options={[{ value: '', label: '全部' }, ...genres.map((item) => ({ value: item, label: item }))]}
      />
      <FilterGroup
        title="地区"
        value={region}
        onPick={(value) => onChange({ region: value })}
        options={[{ value: '', label: '全部' }, ...allRegions.map((item) => ({ value: item, label: item }))]}
      />
      <FilterGroup
        title="年份"
        value={year}
        onPick={(value) => onChange({ year: value })}
        options={[
          { value: '', label: '全部' },
          { value: '2025', label: '2025' },
          { value: '2024', label: '2024' },
          { value: '2023', label: '2023' },
        ]}
      />
      <div className="pt-4 border-t border-background-200">
        <button
          type="button"
          onClick={onReset}
          disabled={activeCount === 0}
          className={`w-full h-9 rounded-md text-xs flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors ${
            activeCount === 0
              ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
              : 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200 cursor-pointer'
          }`}
        >
          <i className="ri-refresh-line" />
          重置筛选{activeCount > 0 ? `（${activeCount}）` : ''}
        </button>
      </div>
    </div>
  );
}

interface CategoryListProps {
  type: string;
}

export default function CategoryListPage({ type }: CategoryListProps) {
  const navigate = useNavigate();
  const params = useParams();
  const resolvedType = params.type ?? type;
  const meta = categoryMeta[resolvedType] ?? categoryMeta.movie;

  const [genre, setGenre] = useState('');
  const [region, setRegion] = useState('');
  const [year, setYear] = useState('');
  const [sort, setSort] = useState<NonNullable<FilmQuery['sort']>>('updated');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setGenre('');
    setRegion('');
    setYear('');
    setSort('updated');
    setPage(1);
    setFilterOpen(false);
  }, [resolvedType]);

  useEffect(() => {
    setPage(1);
  }, [genre, region, year, sort]);

  const results = useMemo<Film[]>(
    () => filterFilms({ type: resolvedType, genre, region, year, sort }),
    [resolvedType, genre, region, year, sort],
  );

  const allOfType = useMemo(() => sortFilms(filterFilms({ type: resolvedType }), 'updated'), [resolvedType]);
  const visible = results.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < results.length;
  const activeCount = [genre, region, year].filter(Boolean).length;

  const patch = (next: { genre?: string; region?: string; year?: string }) => {
    if (next.genre !== undefined) setGenre(next.genre);
    if (next.region !== undefined) setRegion(next.region);
    if (next.year !== undefined) setYear(next.year);
  };

  const reset = () => {
    setGenre('');
    setRegion('');
    setYear('');
  };

  const pickRandom = () => {
    const pool = allOfType.length ? allOfType : [];
    if (!pool.length) return;
    const target = pool[Math.floor(Math.random() * pool.length)];
    navigate(`/detail/${target.id}`);
  };

  return (
    <div className="w-full px-4 md:px-8 pt-8 pb-16">
      <nav aria-label="面包屑" className="flex items-center flex-wrap gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="hover:text-accent-600 transition-colors whitespace-nowrap">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <span className="text-foreground-950 font-medium whitespace-nowrap">{meta.label}</span>
      </nav>

      <header className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
        <div className="max-w-2xl">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center shrink-0">
              <i className={`${meta.icon} text-lg`} />
            </span>
            {meta.label}频道
          </h1>
          <p className="mt-3 text-sm leading-7 text-foreground-600">{meta.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="px-3 py-1.5 rounded-md bg-accent-100 text-accent-900 text-xs whitespace-nowrap">
            共 {results.length} 部结果
          </span>
          <button
            type="button"
            onClick={pickRandom}
            className="h-9 px-3 rounded-md bg-background-100 text-foreground-700 text-xs flex items-center gap-1.5 whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
          >
            <i className="ri-shuffle-line" />
            随机一部
          </button>
        </div>
      </header>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <aside className="hidden lg:block lg:col-span-3">
          <div className="bg-background-50 rounded-lg border border-background-200 p-5 sticky top-20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground-950 flex items-center gap-1.5">
                <i className="ri-filter-3-line text-accent-600" />
                筛选条件
              </h2>
              <span className="text-[10px] text-foreground-400">演示筛选</span>
            </div>
            <FilterPanel
              genres={meta.genres}
              genre={genre}
              region={region}
              year={year}
              onChange={patch}
              onReset={reset}
              activeCount={activeCount}
            />
          </div>
        </aside>

        <div className="lg:col-span-9 min-w-0">
          <div className="flex items-center justify-between gap-3 pb-4 border-b border-background-200">
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 overflow-x-auto scrollbar-hide">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    sort === option.key
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setFilterOpen(true)}
              className="lg:hidden h-9 px-3 rounded-md bg-background-100 text-foreground-700 text-xs flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-filter-3-line" />
              筛选{activeCount > 0 ? ` ${activeCount}` : ''}
            </button>
            <span className="hidden sm:block text-xs text-foreground-400 whitespace-nowrap">
              显示 {visible.length} / {results.length}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-500">
            {[
              { key: 'genre', value: genre },
              { key: 'region', value: region },
              { key: 'year', value: year },
            ]
              .filter((item) => item.value)
              .map((item) => (
                <span key={item.key} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800">
                  {item.value}
                </span>
              ))}
          </div>

          {results.length === 0 ? (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center px-4">
              <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center">
                <i className="ri-film-line text-2xl" />
              </span>
              <p className="mt-4 text-sm font-medium text-foreground-950">暂无符合条件的影视作品</p>
              <p className="mt-2 text-xs text-foreground-500">试试减少筛选条件，或重置后重新浏览</p>
              <button
                type="button"
                onClick={reset}
                className="mt-5 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8">
                {visible.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>

              <div className="mt-10 flex flex-col items-center gap-3">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setPage((prev) => prev + 1)}
                    className="h-10 px-6 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-800 flex items-center gap-2 whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line" />
                    加载更多（剩余 {results.length - visible.length} 部）
                  </button>
                ) : (
                  <p className="text-xs text-foreground-400">已经到底啦 · 共 {results.length} 部作品</p>
                )}
                <div className="flex items-center gap-1 pt-2">
                  {Array.from({ length: Math.ceil(results.length / PAGE_SIZE) }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setPage(index + 1)}
                      className={`w-8 h-8 rounded-md text-xs cursor-pointer transition-colors ${
                        page === index + 1
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 bg-primary-950/50"
          />
          <div className="relative w-full bg-background-50 rounded-t-lg p-5 max-h-[80vh] overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="w-8 h-8 rounded-md bg-background-100 flex items-center justify-center text-foreground-600 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <FilterPanel
              genres={meta.genres}
              genre={genre}
              region={region}
              year={year}
              onChange={patch}
              onReset={reset}
              activeCount={activeCount}
            />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-5 w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              查看 {results.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}